import React from 'react';
import { required, DateField, SaveButton, Toolbar, FormDataConsumer, SelectInput, SimpleForm, ReferenceInput, ReferenceArrayInput,
  AutocompleteArrayInput, AutocompleteInput, NumberInput, Edit, Create, BooleanInput, EditButton, Filter, List, Datagrid, NumberField, TextField,
  TextInput, BooleanField, CheckboxGroupInput, email, TabbedForm, FormTab, usePermissions } from 'react-admin';
import { useFormState, useForm } from 'react-final-form';

const roles = require('./components/roles.json');

const resources = [
  { id: 'networks', name: 'Networks' },
  { id: 'publishers', name: 'Publishers' },
  { id: 'sites', name: 'Sites' },
  { id: 'placements', name: 'Placements' },
  { id: 'batch', name: 'Batch Sites' },
  { id: 'batchp', name: 'Batch Placements' },
  { id: 'gam_accounts', name: 'Gam Accounts' },
  { id: 'advertisers', name: 'Advertisers' },
  { id: 'adcampaigns', name: 'Campaigns' },
  { id: 'creatives', name: 'Creatives' },
  { id: 'segments', name: 'Segments' },
  { id: 'campaigns', name: 'Traffic Rules' },
  { id: 'ad_quality_rules', name: 'Ad Quality Rules' },
  { id: 'deals', name: 'Deals' },
  { id: 'bidders', name: 'Bidders' },
  { id: 'predictions', name: 'Bid Predictions' },
  { id: 'adstxtstatus', name: 'Ads.txt' },
  { id: 'ivt', name: 'IVT Report' },
  { id: 'adcost', name: 'Request Report' },
  { id: 'topsites', name: 'Market Movers Report' },
  { id: 'realtime', name: 'Realtime Dash Report' },
  { id: 'pubreports', name: 'Earning Reports' },
  { id: 'clicks', name: 'Click Reports' },
  { id: 'auctions', name: 'Auction Reports' },
  { id: 'auction_optimizer', name: 'Auction Optimizer' },
  { id: 'discrepancies', name: 'Discrepancy Reports' },
  { id: 'adViewability', name: 'Viewability Reports' },
  { id: 'users', name: 'Users' },
  { id: 'logs', name: 'Logbook' },
  { id: 'settings', name: 'Settings' },
  { id: 'orders', name: 'Orders' },
  { id: 'line_items', name: 'Line Items' },
  { id: 'adquality_reports', name: 'Ad Quality Reports' },
  { id: 'advertiser_account', name: 'Account & Spend Limit (advertiser)' },
  // NBI-39. Two registries have to agree before a resource is grantable: this
  // list supplies the checkbox {id, name}, and roles.json authorizedResources
  // decides which of them a given role may be granted. ResourceCheckboxes
  // intersects the two, so a resource present in only one is silently
  // ungrantable - which is exactly how this one shipped invisible.
  { id: 'advertiser_ip_lists', name: 'IP Lists (advertiser)' }
];

// Resources that render as children of the "Delivery" menu parent (the
// `menuParent: 'delivery'` entries in App.js). Granting one of these without
// also granting `delivery` produces a user who sees NOTHING: the parent only
// registers when `delivery` itself is authorized, and ra-treemenu drops children
// whose parent is absent. So `delivery` is never offered as a checkbox - it is
// not a feature, it is a container - and is implied on save instead.
//
// Duplicated from App.js rather than imported: App.js imports this file, so
// reading it back would be a cycle. Keep in step if a new resource is given
// menuParent: 'delivery'.
const DELIVERY_CHILDREN = [
  'bidders', 'campaigns', 'ad_quality_rules', 'adcampaigns', 'creatives',
  'advertiser_account', 'audiences', 'deals', 'advertisers', 'experiments', 'ip_lists'
];

// Adds `delivery` when any of its children is granted, so ops cannot
// accidentally save a user whose every granted screen is unreachable.
// Idempotent, and a no-op for a user with no delivery children.
export const applyImpliedMenuParents = (data) => {
  const granted = (data && data.resources) || [];
  if (!Array.isArray(granted) || granted.length === 0) return data;
  const needsDelivery = granted.some(r => DELIVERY_CHILDREN.includes(r));
  if (!needsDelivery || granted.includes('delivery')) return data;
  return { ...data, resources: [...granted, 'delivery'] };
};

const roleDefaultResources = (role, modifiedResources) => {
  if (modifiedResources) return modifiedResources;
  return role ? roles[role].defaultResources : [];
}

const ResourceCheckboxes = () => {
  const { values } = useFormState();
  const contextResources = () => {
    if (values.role) {
      return resources.filter(re => roles[values.role].authorizedResources.includes(re.id))
    } else {
      return []
    }
  }

  return values.role && contextResources().length > 0 ? <CheckboxGroupInput source="resources" choices={contextResources()}/> : null;
}

const OrganizationPicker = () => {
  const { permissions } = usePermissions();
  if (!permissions || permissions.role !== 'super_admin') return null;
  return (
    <FormDataConsumer>
      {({ formData }) => formData.role && formData.role !== 'super_admin' &&
        <ReferenceInput source="organization_id" label="Organization"
          reference="organizations"
          perPage={100}
          sort={{ field: 'name', order: 'ASC' }}
          filterToQuery={searchText => ({ search_name: searchText })}
          validate={required()}>
          <AutocompleteInput optionText="name" optionValue="code" style={{ width: '40%' }} />
        </ReferenceInput>
      }
    </FormDataConsumer>
  );
};

const roleChoices = (permissions) => {
  const choices = [
    { id: 'admin', name: 'Admin' },
    { id: 'publisher', name: 'Publisher' },
    { id: 'report_admin', name: 'Accountant' },
    { id: 'bidder', name: 'Bidder' },
    { id: 'advertiser', name: 'Advertiser' },
  ];
  if (permissions && permissions.role === 'super_admin') {
    choices.unshift({ id: 'super_admin', name: 'Super Admin' });
  }
  return choices;
};

const RoleSelect = () => {
  const form = useForm();
  const { permissions } = usePermissions();
  const updateCheckboxes = (e) => {
    form.change('resources', [])
  }
  return (
    <SelectInput source="role" label="Role" validate={required()} onChange={updateCheckboxes}
    choices={roleChoices(permissions)} />
  )
}

const UserFilter = (props) => {
    const { permissions } = usePermissions();
    return (
        <Filter {...props}>
            <TextInput label="Search" source="search_username" alwaysOn resettable />
            <SelectInput label="Role" source="role" alwaysOn allowEmpty choices={roleChoices(permissions)} />
        </Filter>
    );
};

const CustomDateField = ({ record }) => {
    const d = (record.last_login) ? record.last_login : "Not available";
    let date = d.substring(0, d.indexOf('T'));
    let time = d.substring(d.indexOf('T') + 1, d.indexOf('.'));
    return <span><nobr>{date}</nobr><br /><nobr style={{ fontSize: 'x-small' }}>{time}</nobr></span>;
};

export const UserList = (props) => (
    <List {...props} filters={<UserFilter />} perPage={25} bulkActionButtons={false}>
        <Datagrid rowClick="edit">
            <TextField source="name" label="Full name" />
            <TextField source="username" label="Username" />
            <TextField source="email" label="Email" />
            <TextField source="role" label="Role" />
            <NumberField source="organization_id" label="Org ID" />
            <BooleanField source="active" label="Active" />
            <CustomDateField label="Last Login" />
            <TextField source="login_count" label="Login Count" />
            <EditButton variant="contained" color="primary" />
        </Datagrid>
    </List>
);

const UserTitle = ({ record }) => {
    return <span>User {record ? `"${(record.name) ? record.name : record.username}"` : ''} {record && record.active ? 'ACTIVE' : 'INACTIVE'}</span>;
};

const CustomToolbar = props => (
    <Toolbar {...props}>
        <SaveButton />
        <span style={{ paddingLeft: '20px' }}>Last update </span><DateField source="updated_at" label="Update date" showTime style={{ paddingLeft: '10px' }} />
        <span style={{ paddingLeft: '20px' }}>Last login </span><DateField source="last_login" label="Last login date" showTime style={{ paddingLeft: '10px' }} />
        <span style={{ paddingLeft: '20px' }}>Login Count </span><NumberField source="login_count" label="Login count" style={{ paddingLeft: '10px' }} />
    </Toolbar>
);

export const UserEdit = (props) => (
    <Edit title={<UserTitle />} {...props} transform={applyImpliedMenuParents}>
        <TabbedForm redirect={false} toolbar={<CustomToolbar /> } {...props} validateOnBlur={true}>
          <FormTab label="Settings">
            <BooleanInput source="active" label="Active" defaultValue={true} validate={required()} />
            <TextInput source="name" label="Full name" validate={required()} />
            <TextInput source="username" label="Username" validate={required()} />
            <TextInput source="update_password" label="Password" helperText="Enter new password here if you want to update the existing password. Otherwise leave this field blank." style={{ width: '40%' }} />
            <TextInput source="email" label="Email" validate={[required(), email()]} />
            <NumberInput source='sales_commission' label='Sales Commission' helperText='Sales Commission off the PROFIT.' />
            <OrganizationPicker/>
            <RoleSelect/>
            <ResourceCheckboxes/>

            {/* <ReferenceInput source="network_id" label="Network"
                reference="networks"
                perPage={10}
                sort={{ field: 'name', order: 'ASC' }}
                filterToQuery={searchText => ({ search_name: searchText })}
                allowEmpty>
                <AutocompleteArrayInput label="Network" optionText="name" optionValue="code" style={{ width: '40%' }} />
            </ReferenceInput> */}

            <FormDataConsumer>
                {({ formData, ...rest }) => formData.role === 'publisher' &&
                    [<ReferenceArrayInput source="publishers"
                        validate={required()}
                        reference="publishers"
                        basePath='publishers'
                        perPage={10}
                        sort={{ field: 'name', order: 'ASC' }}
                        filterToQuery={searchText => ({ search_name: searchText })}
                        allowEmpty>
                        <AutocompleteArrayInput label="Publishers" optionText="name" optionValue="code" style={{ width: '90%' }} />
                    </ReferenceArrayInput>,
                    <ReferenceArrayInput source="publisher_bidders"
                        reference="bidders"
                        basePath='bidders'
                        perPage={10}
                        sort={{ field: 'name', order: 'ASC' }}
                        filterToQuery={searchText => ({ search_name: searchText })}
                        allowEmpty>
                        <AutocompleteArrayInput label="Bidders" optionText={(record) => record?.name ? `${record.name} (${record.code})` : '- None -'} optionValue="code" style={{ width: '90%' }} 
                            helperText='API reports will be slipt by the selected Bidders.'/>
                    </ReferenceArrayInput>
                    ]
                }
            </FormDataConsumer>
            <FormDataConsumer>
                {({ formData, ...rest }) => formData.role === 'bidder' &&
                    <ReferenceArrayInput source="bidders" label="Bidder"
                        reference="bidders"
                        basePath='bidders'
                        perPage={15}
                        sort={{ field: 'name', order: 'ASC' }}
                        filterToQuery={searchText => ({ search_name: searchText })}
                        allowEmpty>
                        <AutocompleteArrayInput label="Bidders" optionText={choice => choice && choice.name ? `${choice.name} (${choice.code})` : ''} optionValue="code" style={{ width: '90%' }} />
                    </ReferenceArrayInput>
                }                
            </FormDataConsumer>
            <FormDataConsumer>
                {({ formData, ...rest }) => formData.role === 'advertiser' &&
                    <ReferenceArrayInput source="advertisers" label="Advertisers"
                        reference="advertisers"
                        basePath='advertisers'
                        perPage={15}
                        sort={{ field: 'name', order: 'ASC' }}
                        filterToQuery={searchText => ({ search_name: searchText })}
                    >
                        <AutocompleteArrayInput label="Advertisers" optionText={choice => choice && choice.name ? `${choice.name}` : ''} optionValue="code" style={{ width: '90%' }} />
                    </ReferenceArrayInput>
                }
            </FormDataConsumer>
            <hr style={{ width: '100%' }} />
            <TextField source="api_token" label="API Token" />
            <BooleanInput source="force_update_api_token" label="Force update API token" defaultValue={false} helperText="Note that updating the API token will break the APIs connecting using the existing token." style={{ width: '40%' }} />
          </FormTab>
          <FormTab label="User Preferences">
            <SelectInput source="preferences.homepage" label="Default Dashboard"
            defaultValue={null}
            choices={[
                {id:'main', name: 'Main Dashboard'},
                {id:'carbon', name: 'Analytics Dasboard'}
            ]} />
          </FormTab>
        </TabbedForm>
    </Edit>
);

export const UserCreate = (props) => (
    <Create title="Create User" {...props} transform={applyImpliedMenuParents}>
        <SimpleForm toolbar={<CustomToolbar />} >
            <BooleanInput source="active" label="Active" validate={required()} defaultValue={true} />
            <TextInput source="name" label="Full name" validate={required()} />
            <TextInput source="username" label="Username" validate={required()} />
            <TextInput source="password" label="Password" validate={required()} />
            <TextInput source="email" label="Email" validate={[required(), email()]} />
            <BooleanInput source="notify_user" label="Send a Welcome email to user" defaultValue={true} style={{width: '17%'}}/>
            <OrganizationPicker/>
            <RoleSelect/>
            <ResourceCheckboxes/>
            {/* <FormDataConsumer>
                {({ formData, ...rest }) => formData.role === 'network_manager' &&
                    <ReferenceInput source="network_id" label="Network"
                        reference="networks"
                        perPage={10}
                        validate={required()}
                        sort={{ field: 'name', order: 'ASC' }}
                        filterToQuery={searchText => ({ search_name: searchText })}
                        allowEmpty>
                        <AutocompleteArrayInput label="Network" optionText="name" optionValue="code" style={{ width: '40%' }} />
                    </ReferenceInput>
                }
            </FormDataConsumer> */}
            <FormDataConsumer>
                {({ formData, ...rest }) => formData.role === 'publisher' &&
                    <ReferenceArrayInput source="publishers" label="Publisher"
                        validate={required()}
                        reference="publishers"
                        basePath='publishers'
                        perPage={10}
                        sort={{ field: 'name', order: 'ASC' }}
                        filterToQuery={searchText => ({ search_name: searchText })}
                        allowEmpty>
                        <AutocompleteArrayInput label="Publishers" optionText="name" optionValue="code" style={{ width: '90%' }} />
                    </ReferenceArrayInput>
                }
            </FormDataConsumer>
            <FormDataConsumer>
                {({ formData, ...rest }) => formData.role === 'bidder' &&
                    <ReferenceArrayInput source="bidders" label="Bidder"
                        reference="bidders"
                        basePath='bidders'
                        perPage={15}
                        sort={{ field: 'name', order: 'ASC' }}
                        filterToQuery={searchText => ({ search_name: searchText })}
                        allowEmpty>
                        <AutocompleteArrayInput label="Bidders" optionText={choice => choice && choice.name ? `${choice.name} (${choice.code})` : ''} optionValue="code" style={{ width: '90%' }} />
                    </ReferenceArrayInput>
                }
            </FormDataConsumer>
            <FormDataConsumer>
                {({ formData, ...rest }) => formData.role === 'advertiser' &&
                    <ReferenceArrayInput source="advertisers" label="Advertisers"
                        reference="advertisers"
                        basePath='advertisers'
                        perPage={15}
                        sort={{ field: 'name', order: 'ASC' }}
                        filterToQuery={searchText => ({ search_name: searchText })}
                    >
                        <AutocompleteArrayInput label="Advertisers" optionText={choice => choice && choice.name ? `${choice.name}` : ''} optionValue="code" style={{ width: '90%' }} />
                    </ReferenceArrayInput>
                }
            </FormDataConsumer>
        </SimpleForm>
    </Create >
);